import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { onMaintenanceChange } from '../services/api'
import { fetchPublicPlatformConfig } from '../services/publicPlatformConfig'
import MaintenancePage from '../pages/MaintenancePage'

const MaintenanceContext = createContext(null)

export const MaintenanceProvider = ({ children }) => {
  const { user } = useAuth()
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)
  const [maintenanceMessage, setMaintenanceMessage] = useState(null)

  // Check maintenance status on mount via raw fetch (no auth dependency)
  useEffect(() => {
    const checkMaintenance = async () => {
      const data = await fetchPublicPlatformConfig('/platform/maintenance')
      if (data?.enabled) {
        setIsMaintenanceMode(true)
        setMaintenanceMessage(data.message || null)
      }
    }

    checkMaintenance()
  }, [])

  // Listen for real-time 503 maintenance detection from API interceptor
  useEffect(() => {
    const unsubscribe = onMaintenanceChange((state) => {
      if (state.active) {
        setIsMaintenanceMode(true)
        setMaintenanceMessage(state.message)
      }
    })

    return unsubscribe
  }, [])

  const isSuperAdmin = user?.isSuperAdmin === true

  const value = {
    isMaintenanceMode,
    maintenanceMessage
  }

  // When maintenance is active and user is NOT super admin, show maintenance page
  if (isMaintenanceMode && !isSuperAdmin) {
    return (
      <MaintenanceContext.Provider value={value}>
        <MaintenancePage message={maintenanceMessage} />
      </MaintenanceContext.Provider>
    )
  }

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  )
}

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext)
  if (!context) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider')
  }
  return context
}

export default MaintenanceContext
